import { collection, doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import { CourseTee, USGA_COURSE_IMPORT_STATUSES, UsgaCourseImport } from '../types/Course';

export default function useCourseUsgaMutators() {
  const { isGod } = useAuth();
  const { userState } = useData();

  /**
   * Asks the server to re-read the course's USGA tee sets and posting season
   * (handleCourseUsgaImportRequested). With usgaCourseId, links the course to that USGA course first,
   * in the same write.
   */
  const requestUsgaImport = async (courseId: string, usgaCourseId?: string): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only god users can import USGA course data');
      return;
    }
    const requestedBy = userState.status === 'ready' ? userState.userProfile?.itemId ?? null : null;
    const usgaImport: UsgaCourseImport = {
      status: USGA_COURSE_IMPORT_STATUSES.REQUESTED,
      requestedBy,
      requestedAt: Timestamp.now(),
      finishedAt: null,
      teeCount: null,
      message: null,
    };
    await updateDoc(doc(firestore, 'course', courseId), usgaCourseId ? { usgaCourseId, usgaImport } : { usgaImport });
  };

  const updateUsgaTees = async (courseId: string, tees: CourseTee[]): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only god users can update USGA tee data');
      return;
    }
    const usgaRef = doc(collection(firestore, 'course', courseId, 'usga'), 'data');
    await updateDoc(usgaRef, { tees });
  };

  /**
   * Writes a new course's USGA tee sets (course/{courseId}/usga/data) from an import draft, so the
   * course starts out linked exactly as a later "Update from USGA" would leave it.
   */
  const createUsgaData = async (courseId: string, tees: CourseTee[], usgaCourseId: string): Promise<void> => {
    if (!isGod) {
      console.error('Permission denied: only god users can write USGA course data');
      return;
    }
    const usgaRef = doc(collection(firestore, 'course', courseId, 'usga'), 'data');
    await setDoc(usgaRef, { tees, ghinCourseId: parseInt(usgaCourseId, 10), importedAt: Timestamp.now() });
  };

  return { requestUsgaImport, updateUsgaTees, createUsgaData };
}
